import { NavLink, Outlet, useLocation, Link, useSearchParams } from 'react-router-dom';
import { Home, GraduationCap, Users, Calendar, BarChart3, LogOut, ArrowRightLeft, Mail, Crown, UserCog, MessageSquare, DollarSign, Truck, User, FileText, Heart, Video, AlertTriangle, ChevronDown, LifeBuoy, Inbox, ClipboardCheck, Trophy, Bell, Sparkles, UserPlus, PenSquare, Bookmark, Map, Megaphone, FolderTree, ShieldCheck, Shield, Menu, X, Scale, TrendingUp, Wallet, Lock, Presentation, Radio, Camera,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useUser, useLogout } from '@/lib/auth';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { useIsAdmin, usePrimaryRole, useCapabilities, type UserCapabilities } from '@/lib/hooks/useUserRoles';
import { GuruSetupBar } from '@/components/guru/GuruSetupBar';
import type { AdminCapability } from '@gurumba/schemas';
import { useMySubscription } from '@/lib/api/subscription';
import { useMyProfile } from '@/lib/api/account';
import { useRecordActivity } from '@/lib/api/gamification';
import { toast } from 'sonner';
import { Logo } from '@/components/ui/logo';
import { NotificationBell } from '@/components/layout/NotificationBell';
import { ComposeModal } from '@/components/community/ComposeModal';
import { PLAN_TIER_COLOR } from '@/components/domain/PlanFamilyCard';
import { useFreePlanOpen } from '@/lib/hooks/useBetaActive';
import { UPGRADE_PATH } from '@/lib/hooks/useMembership';
import { VerifyEmailBanner } from '@/components/domain/VerifyEmailBanner';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  end?: boolean;
  /** Sprint 10.23 — capability requerida para ver este item. Si está vacío,
   *  todos los con acceso al panel admin lo ven. admin SIEMPRE ve todo. */
  requires?: AdminCapability;
  /** Sub-item visual (indentado, más chico). Ej: filtros de comunidad. */
  indent?: boolean;
  /** Se renderiza como botón de acción (abre el composer) en vez de link normal. */
  cta?: boolean;
  /** Link destacado en verde (destino principal). Navega como link normal. */
  highlight?: boolean;
  /** Freemium: el Free lo ve con candado; al entrar ve la vista previa y el upgrade. */
  locked?: boolean;
  /** Etiqueta corta a la derecha (ej. "Recomendado"). */
  tag?: string;
  /** Otras rutas en las que este item queda marcado (ej. el detalle de un curso). */
  activeAlso?: string[];
}
interface NavSection {
  section: string;
  items: NavItem[];
  /** Arranca contraída (si el user no la abrió antes). */
  collapsedByDefault?: boolean;
  /** Muestra un candado junto al título de la sección. */
  locked?: boolean;
}

// Sub-items de Comunidad: un botón para postear y "Mi actividad" (una sola
// entrada que abre la vista con selector de Mis posts / Me gusta / Insights /
// Preguntas — así no alargamos el menú con 4 sub-clasificaciones).
const COMMUNITY_SUBNAV: NavItem[] = [
  { to: '/community?compose=1', label: 'Postear', icon: PenSquare, indent: true, cta: true },
  { to: '/community?mine=posts', label: 'Mi actividad', icon: Bookmark, indent: true },
];

// Sección "Inicio" = Comunidad como puerta de entrada social. Va arriba de
// todo, sin encabezado de sección (section vacío), con Postear y Mi actividad
// como sub-items. Compartida por cliente y guru.
const COMMUNITY_HOME_SECTION: NavSection = {
  section: '',
  items: [
    { to: '/community', label: 'Comunidad', icon: MessageSquare, highlight: true },
    ...COMMUNITY_SUBNAV,
  ],
};

const CLIENT_NAV: NavSection[] = [
  COMMUNITY_HOME_SECTION,
  {
    section: 'Aprendizaje',
    items: [
      // "Mi Hub" = el antiguo "Inicio" (/dashboard): el panel personal.
      { to: '/dashboard', label: 'Mi Hub', icon: Home, end: true },
      // Cursos en vivo (2026-10-06): el producto que se empuja, arriba.
      { to: '/dashboard/cursos-en-vivo', label: 'Cursos en vivo', icon: Radio, tag: 'Nuevo' },
      { to: '/dashboard/courses', label: 'Cursos', icon: GraduationCap },
      // Caminos (G9): paths curados que agrupan cursos por objetivo.
      // Su propia ruta /dashboard/paths para que no quede seleccionado
      // junto con "Cursos" (ambos a /dashboard/courses generaba el dual
      // highlight en el sidebar).
      { to: '/dashboard/paths', label: 'Paths', icon: Map },
      // Eventos: post-review (Marco) movido de "Tribu" → Aprendizaje porque
      // las charlas en vivo son contenido educativo, no networking social.
      { to: '/dashboard/events', label: 'Eventos', icon: Calendar },
      // Sprint 10.50 — historial académico personal del estudiante:
      // lista de cursos inscritos con progreso, nota final y certificados.
      { to: '/dashboard/my-courses', label: 'Mi historial', icon: ClipboardCheck },
      { to: '/dashboard/favorites', label: 'Favoritos', icon: Heart },
    ],
  },
  {
    section: 'Mentoría',
    items: [
      { to: '/dashboard/mentors', label: 'Gurus', icon: Users },
      { to: '/dashboard/sessions', label: 'Mis sesiones', icon: Video },
    ],
  },
  {
    section: 'Mi progreso',
    items: [
      { to: '/dashboard/achievements', label: 'Logros', icon: Trophy },
      { to: '/dashboard/referrals', label: 'Referidos', icon: UserPlus },
      { to: '/dashboard/notifications', label: 'Notificaciones', icon: Bell },
    ],
  },
  // NOTA: la sección "Tribu" fue eliminada post-review (Marco). Eventos pasó
  // a Aprendizaje. Comunidad ya vive como sección "Inicio" arriba de todo.
  // La sección "Cuenta" (Mi cuenta + Soporte) vive en el dropdown del avatar.
];

// Cliente SIN plan (circuito de sesión suelta): el sidebar solo habilita lo
// que de verdad puede usar — la parrilla de gurus y sus sesiones. Todo lo
// demás (comunidad, cursos, eventos) es membresía: en vez de mostrar puertas
// que rebotan, se ofrece un upgrade claro (card debajo del nav) que lleva a
// Mi cuenta → Suscripción, donde paga sin salir al sitio público.
// Soporte y Mi cuenta ya viven en el dropdown del avatar, como para todos.
const SESSION_ONLY_NAV: NavSection[] = [
  {
    section: 'Mentoría',
    items: [
      // La entrada a un curso en vivo no pide plan: también va aquí.
      { to: '/dashboard/cursos-en-vivo', label: 'Cursos en vivo', icon: Radio, tag: 'Nuevo' },
      { to: '/dashboard/mentors', label: 'Gurus', icon: Users },
      { to: '/dashboard/sessions', label: 'Mis sesiones', icon: Video },
    ],
  },
];

// Cliente Free (freemium, beta cerrada): menú corto con lo que puede usar
// (Aprendizaje y Mentoría). Lo de miembros (Comunidad, Logros, Referidos) va
// junto en "Solo miembros", con candado y contraído: así el cuadro "Desbloquea
// todo" se ve sin hacer scroll. Notificaciones queda en la campana del header.
// Cursos, Paths y Eventos abiertos: adentro cada curso/evento de miembros
// lleva su propio candado.
const LOCKED_FOR_FREE = new Set(['/community', '/dashboard/achievements', '/dashboard/referrals']);
const FREE_CLIENT_NAV: NavSection[] = [
  ...CLIENT_NAV
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          !COMMUNITY_SUBNAV.includes(item) &&
          !LOCKED_FOR_FREE.has(item.to) &&
          item.to !== '/dashboard/notifications',
      ),
    }))
    .filter((section) => section.items.length > 0),
  {
    section: 'Solo miembros',
    locked: true,
    collapsedByDefault: true,
    items: CLIENT_NAV.flatMap((section) => section.items)
      .filter((item) => LOCKED_FOR_FREE.has(item.to))
      .map((item) => ({ ...item, locked: true, highlight: false })),
  },
];

const GURU_NAV: NavSection[] = [
  COMMUNITY_HOME_SECTION,
  {
    section: 'Mi panel',
    items: [
      // "Mi Hub" = el panel del guru (/guru), antes "Inicio".
      { to: '/guru', label: 'Mi Hub', icon: Home, end: true },
      { to: '/guru/sessions', label: 'Mis sesiones', icon: Video },
      { to: '/guru/availability', label: 'Disponibilidad', icon: Calendar },
      // Mis cursos (2026-10-05): un solo lugar para los en vivo y los grabados.
      // Cómo hacer uno nuevo se elige una vez, en /guru/courses/proponer.
      { to: '/guru/courses', label: 'Mis cursos', icon: GraduationCap, activeAlso: ['/guru/curso-en-vivo'] },
      // Cola de tareas pendientes de calificar — el hook filtra a las del
      // dueño del curso (el guru solo ve sus propias tareas).
      { to: '/guru/grading', label: 'Por calificar', icon: ClipboardCheck },
      { to: '/guru/settlements', label: 'Mis liquidaciones', icon: DollarSign },
      { to: '/guru/notifications', label: 'Notificaciones', icon: Bell },
    ],
  },
  // Para gurus también: Eventos pasó a la sección Mi panel arriba si querés
  // ofrecerlo, pero por defecto no lo mostramos porque el guru gestiona su
  // propia agenda. Eliminamos la sección Tribu (Eventos) post-review.
  //
  // NOTA: "Logros" (gamification) es feature de cliente — vive bajo
  // /dashboard/* (ClientGuard) y no aplica al guru, por eso se quitó del nav.
  // La sección "Cuenta" (Mi cuenta + Soporte) vive en el dropdown del avatar.
];

// Sprint 10.60 — Reorganización gerencial del panel admin.
// Reglas: (1) SOLO rutas /admin/* — nada de /community, /dashboard/* ni /guru/*
// que sacarían al admin a otro contexto (para ver el entorno cliente está el
// switcher "Ver como cliente"). (2) Agrupado por temas para lectura gerencial.
const ADMIN_NAV: NavSection[] = [
  {
    // 24-sep-2026 — lo que ven los socios. Indicadores + Tracción = socio de
    // indicadores (partner_viewer); Finanzas = socio de finanzas
    // (partner_finance). El admin ve las tres.
    section: 'Negocio',
    items: [
      { to: '/admin', label: 'Indicadores', icon: BarChart3, end: true, requires: 'partner_viewer' },
      { to: '/admin/traccion', label: 'Tracción externa', icon: TrendingUp, requires: 'partner_viewer' },
      { to: '/admin/finanzas', label: 'Finanzas', icon: Wallet, requires: 'partner_finance' },
    ],
  },
  {
    section: 'Personas',
    items: [
      { to: '/admin/users', label: 'Usuarios', icon: Users },
      { to: '/admin/mentors', label: 'Gurus', icon: UserCog },
      // Fotos que mandan los gurus: se editan antes de publicarse (25-oct-2026).
      { to: '/admin/fotos', label: 'Fotos de gurus', icon: Camera },
      { to: '/admin/invitations', label: 'Invitaciones', icon: Mail },
      // Solo admin: la página tiene el botón "Cerrar beta" (24-sep-2026).
      { to: '/admin/founders', label: 'Founders', icon: Crown },
    ],
  },
  {
    section: 'Contenido',
    items: [
      { to: '/admin/cursos-en-vivo', label: 'Cursos en vivo', icon: Presentation },
      { to: '/admin/courses', label: 'Cursos', icon: GraduationCap, requires: 'content_editor' },
      { to: '/admin/courses/review', label: 'Cola de revisión', icon: AlertTriangle, requires: 'content_editor' },
      { to: '/admin/courses/categories', label: 'Categorías', icon: FolderTree, requires: 'content_editor' },
      { to: '/admin/achievements', label: 'Logros', icon: Crown, requires: 'content_editor' },
      { to: '/admin/gamification', label: 'Gamificación', icon: Sparkles },
    ],
  },
  {
    section: 'Comunidad y Radar',
    items: [
      // El admin debe poder VER lo que hacen los usuarios en la comunidad.
      // /community no es /admin/* pero el admin tiene acceso (SubscriptionGuard
      // deja pasar al admin). Es visibilidad, no moderación.
      { to: '/community', label: 'Ver comunidad', icon: MessageSquare },
      { to: '/admin/community-moderation', label: 'Moderación', icon: AlertTriangle, requires: 'moderator' },
      { to: '/admin/radar-social', label: 'Radar social', icon: Megaphone },
      { to: '/admin/events', label: 'Gestión de eventos', icon: Calendar },
    ],
  },
  {
    section: 'Finanzas',
    items: [
      { to: '/admin/payments', label: 'Liquidaciones', icon: DollarSign },
      { to: '/admin/proveedores', label: 'Proveedores', icon: Truck },
      { to: '/admin/tax-review', label: 'Tax review', icon: FileText },
      { to: '/admin/legal', label: 'Documentos legales', icon: Scale },
    ],
  },
  {
    section: 'Soporte',
    items: [
      { to: '/admin/tickets', label: 'Tickets', icon: LifeBuoy, requires: 'support_agent' },
      { to: '/admin/leads', label: 'Leads', icon: Inbox, requires: 'support_agent' },
    ],
  },
  {
    section: 'Auditoría',
    items: [
      { to: '/admin/audit', label: 'Registro de auditoría', icon: ShieldCheck },
      { to: '/admin/security', label: 'Centinela de seguridad', icon: Shield },
      { to: '/admin/webhook-failures', label: 'Webhook failures', icon: AlertTriangle },
    ],
  },
];

/** Filtra secciones+items del ADMIN_NAV según capabilities del user.
 *  Reglas:
 *  - admin → ve todo (no se filtra nada).
 *  - sub-rol → ve items con requires==undefined (público admin) Y los que
 *    matchean su capability.
 *  - Si una sección queda vacía después del filtro, se descarta.
 */
function filterAdminNavByCaps(nav: NavSection[], caps: UserCapabilities): NavSection[] {
  if (caps.isAdmin) return nav;
  const filtered: NavSection[] = [];
  for (const section of nav) {
    const items = section.items.filter((item) => {
      if (!item.requires) {
        // Items sin requires son solo-admin (tax-review, payments, webhooks, users, etc.)
        // Solo admin los ve.
        return false;
      }
      if (item.requires === 'moderator')      return caps.isModerator;
      if (item.requires === 'support_agent')  return caps.isSupportAgent;
      if (item.requires === 'partner_viewer') return caps.isPartnerViewer;
      if (item.requires === 'partner_finance') return caps.isPartnerFinance;
      if (item.requires === 'content_editor') return caps.isContentEditor;
      return false;
    });
    if (items.length > 0) filtered.push({ ...section, items });
  }
  return filtered;
}

export function DashboardLayout({ children }: { children?: React.ReactNode } = {}) {
  const user = useUser();
  const logout = useLogout();
  const isAdmin = useIsAdmin();
  const primaryRole = usePrimaryRole();
  const caps = useCapabilities();
  const { data: subscription, isLoading: subLoading } = useMySubscription();
  const { data: myProfile, isLoading: profileLoading } = useMyProfile();
  const recordActivity = useRecordActivity();
  const location = useLocation();
  // Drawer del sidebar en mobile (en desktop el aside es fijo).
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Cerrar el drawer al navegar (cambio de ruta o de query).
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname, location.search]);

  // Registrar la actividad diaria UNA VEZ por sesión, en el shell que envuelve
  // a TODAS las pantallas autenticadas (incluida /community, que es donde
  // aterriza el user al entrar). Antes vivía solo en /dashboard, así que la
  // racha quedaba congelada si el user nunca abría esa pantalla. Idempotente
  // por día del lado del server.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await recordActivity.mutateAsync();
        if (cancelled || !result?.extended) return;
        if (result.current_streak > 1) {
          toast.success(`¡${result.current_streak} días seguidos! 🔥`, {
            description: 'Sigue así para desbloquear nuevos logros.',
          });
        }
      } catch {
        /* silent: si falla, la app sigue funcionando */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  // Modal "Postear" global: lo dispara el botón del sidebar con ?compose=1
  // sobre cualquier pantalla, sin navegar a Comunidad.
  const composeOpen = searchParams.get('compose') === '1';
  const composePrefill = searchParams.get('prefill') ?? undefined;
  // Tema del Radar que disparó el composer (Fase 0 Radar Público).
  const composeTrendId = searchParams.get('trend') ?? undefined;
  function closeCompose() {
    const next = new URLSearchParams(searchParams);
    next.delete('compose');
    next.delete('prefill');
    next.delete('trend');
    setSearchParams(next, { replace: true });
  }

  const inGuruContext = location.pathname.startsWith('/guru');
  const inAdminContext = location.pathname.startsWith('/admin');

  // Sprint 8 — Modelo B (rol único exclusivo):
  //   - Guru ve SOLO GURU_NAV (sin switcher).
  //   - Cliente ve SOLO CLIENT_NAV (sin switcher).
  //   - Admin es la excepción: ve todo y tiene switcher para alternar contextos
  //     (admin/guru/cliente) para hacer QA del producto entero.
  //
  // En rutas compartidas como /community (donde no hay prefijo /guru ni /admin)
  // elegimos el nav según el ROL PRIMARIO del user, no el path — sino un Guru
  // entrando a /community vería el menú de cliente.
  let primaryNav: NavSection[];
  if (inAdminContext) {
    // Sub-admins (sin rol 'admin' completo) ven solo lo que les corresponde
    primaryNav = filterAdminNavByCaps(ADMIN_NAV, caps);
  } else if (inGuruContext) {
    primaryNav = GURU_NAV;
  } else if (primaryRole === 'guru') {
    primaryNav = GURU_NAV;
  } else if (primaryRole === 'admin') {
    primaryNav = ADMIN_NAV;
  } else if (caps.hasAnyAdminAccess && !isAdmin) {
    // Sub-admin (no full admin) en /community o /events → mantenemos client/guru
    // según su rol primario, pero damos acceso a /admin via switcher.
    primaryNav = CLIENT_NAV;
  } else {
    primaryNav = CLIENT_NAV;
  }

  // Cliente sin plan → sidebar reducido (Mentoría) + card de upgrade.
  // Mientras la sub carga mantenemos el nav completo para no parpadear el
  // menú a los suscriptores (mayoría); los links igual están guardados.
  // Freemium: con la beta cerrada ese cliente es Free → menú completo con
  // candados (FREE_CLIENT_NAV) en vez del menú reducido de sesión suelta.
  const { isFreeOpen } = useFreePlanOpen();
  const isClientWithoutPlan =
    primaryNav === CLIENT_NAV &&
    primaryRole === 'user' &&
    !caps.hasAnyAdminAccess &&
    !subLoading &&
    !subscription;
  const isFreeClient = isClientWithoutPlan && isFreeOpen;
  const isSessionOnlyClient = isClientWithoutPlan && !isFreeOpen;
  if (isFreeClient) {
    primaryNav = FREE_CLIENT_NAV;
  } else if (isSessionOnlyClient) {
    primaryNav = SESSION_ONLY_NAV;
  }

  let contextSwitch: { label: string; to: string } | null = null;
  if (isAdmin) {
    // Mostrar switcher al siguiente contexto en rotación: admin → cliente → guru → admin
    if (inAdminContext) contextSwitch = { label: 'Ver como cliente', to: '/dashboard' };
    else if (inGuruContext) contextSwitch = { label: 'Cambiar a panel admin', to: '/admin' };
    else contextSwitch = { label: 'Cambiar a panel admin', to: '/admin' };
  } else if (caps.hasAnyAdminAccess) {
    // Sub-admin (moderador / soporte / partner / editor): solo entrada al panel
    // y vuelta a su panel principal — sin rotación completa.
    if (inAdminContext) {
      // El socio "puro" no necesariamente es miembro: su salida es Mi cuenta.
      contextSwitch = caps.isPartnerOnly
        ? { label: 'Mi cuenta', to: '/account' }
        : { label: 'Volver al inicio', to: primaryRole === 'guru' ? '/guru' : '/dashboard' };
    } else {
      contextSwitch = {
        label: caps.isPartnerOnly ? 'Ir al panel de socios' : 'Ir al panel del equipo',
        to: '/admin',
      };
    }
  }
  // Para guru / cliente sin capabilities: contextSwitch queda en null.

  // Si un Guru no-admin de alguna forma cayó en /dashboard, hubiéramos
  // redirect antes en ClientGuard — acá no hacemos nada extra.
  void primaryRole; // referencia para evitar warning de unused

  // ============ Badge de rol/plan junto al logo ============
  // Prioridad: admin > guru > plan (premium > pro). Free no muestra badge.
  // Colores específicos definidos en branding (Sprint 10.21).
  const userBadge: { label: string; className: string } | null = (() => {
    if (isAdmin) return { label: 'Admin', className: 'bg-brand-primary text-white' };
    if (primaryRole === 'guru') return { label: 'Guru', className: 'bg-brand-energy text-white' };
    if (caps.isPartnerOnly) return { label: 'Socio', className: 'bg-brand-accent text-white' };
    const plan = subscription?.plan_code;
    if (plan === 'premium' || plan === 'premium_founder') {
      return { label: 'Premium', className: `${PLAN_TIER_COLOR.premium} text-white` };
    }
    if (plan === 'pro' || plan === 'pro_founder') {
      return { label: 'En Vivo', className: `${PLAN_TIER_COLOR.pro} text-white` };
    }
    return null;
  })();

  // Contenido del sidebar (secciones + switch contextual). Se reutiliza en el
  // aside fijo de desktop y en el drawer de mobile.
  const sidebarInner = (
    <>
      <div className="flex-1 min-h-0 overflow-y-auto -mr-2 pr-2">
        {primaryNav.map((section) => (
          <NavSectionBlock key={section.section} section={section} collapsible />
        ))}
      </div>
      {/* Upgrade in-app (Free y cliente de sesión suelta): fijo al pie del menú,
          fuera del scroll, para que siempre se vea. El pricing vive adentro
          (Mi cuenta → Suscripción), no lo botamos al sitio público. */}
      {(isSessionOnlyClient || isFreeClient) && (
        <div className="mt-3 flex-shrink-0 rounded-xl bg-gradient-to-br from-brand-primary to-[#0d1f26] p-4 text-white shadow-soft">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 flex-shrink-0 text-brand-accent" />
            <p className="text-sm font-semibold leading-snug">Desbloquea todo</p>
          </div>
          <p className="mt-1 text-xs leading-snug text-white/70">
            Todos los cursos en vivo y la biblioteca completa, por el precio de un curso.
          </p>
          <Link
            to={UPGRADE_PATH}
            className="mt-3 flex items-center justify-center rounded-lg bg-brand-energy px-3 py-2 text-xs font-semibold text-[#071114] transition-opacity hover:opacity-90"
          >
            Mejorar mi plan
          </Link>
        </div>
      )}
      {contextSwitch && (
        <div className="flex-shrink-0 mt-3 pt-3 border-t" style={{ borderColor: '#DCE7EC' }}>
          <NavLink
            to={contextSwitch.to}
            className="flex items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-2 text-xs font-medium transition-colors hover:border-brand-accent"
            style={{ color: '#526978', borderColor: '#DCE7EC', backgroundColor: 'rgba(220, 231, 236, 0.3)' }}
          >
            <ArrowRightLeft className="h-3.5 w-3.5" />
            {contextSwitch.label}
          </NavLink>
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-card">
        <div className="flex h-20 items-center justify-between px-4 md:px-10">
          <div className="flex items-center gap-2 md:gap-3">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg text-brand-text-strong hover:bg-muted/60"
              aria-label="Abrir menú"
            >
              <Menu className="h-5 w-5" />
            </button>
            <Link to="/" className="flex items-center gap-3">
              <Logo variant="horizontal" size={44} />
              {userBadge && (
              <span
                className={cn(
                  // En teléfonos de 360 px el sello "Premium" se montaba sobre la campana.
                  'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider max-[379px]:hidden',
                  userBadge.className,
                )}
              >
                {userBadge.label}
              </span>
            )}
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <AccountMenu
              user={user}
              avatarUrl={myProfile?.avatar_url ?? null}
              fullName={myProfile?.full_name ?? null}
              profileLoading={profileLoading}
              onLogout={() => logout.mutate()}
              loggingOut={logout.isPending}
            />
          </div>
        </div>
      </header>

      {/* La guía del registro vive en el shell, no en el hub: así acompaña al
          guru a TODAS las pantallas y siempre tiene a un clic lo siguiente que
          le falta. Se esconde sola al llegar al 100%.

          Va FUERA del grid de abajo a propósito: como hijo directo de un
          `grid-cols-[240px_1fr]` se colocaba en la celda del contenido y
          empujaba el <main> a la fila siguiente, dentro de la columna de 240px.
          Toda la app quedaba aplastada en una tira angosta. */}
      <GuruSetupBar />

      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr]">
        {/* Sidebar fijo — solo desktop. En mobile va en el drawer. */}
        <aside
          className="hidden md:flex sticky top-20 h-[calc(100vh-5rem)] border-r border-border bg-card p-4 flex-col"
        >
          {sidebarInner}
        </aside>

        <main className="p-4 md:p-8 animate-fade-in text-foreground min-w-0">
          {/* Sprint 10.27: children opcional permite usar el layout desde
              wrappers que deciden la vista (CourseRouteSwitch, etc).
              Si no se pasan children, render normal via React Router Outlet. */}
          {/* Freemium: aviso "Confirma tu correo" (solo Free sin confirmar). */}
          <VerifyEmailBanner />
          {children ?? <Outlet />}
        </main>
      </div>

      {/* Drawer del sidebar en mobile. */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileNavOpen(false)}
          />
          <aside className="absolute left-0 top-0 flex h-full w-[82%] max-w-[300px] flex-col border-r border-border bg-card p-4 shadow-xl">
            <div
              className="mb-1 flex items-center justify-between border-b pb-3"
              style={{ borderColor: '#DCE7EC' }}
            >
              <Logo variant="horizontal" size={36} />
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-brand-text-strong hover:bg-muted/60"
                aria-label="Cerrar menú"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {sidebarInner}
          </aside>
        </div>
      )}

      {composeOpen && !isFreeClient && <ComposeModal onClose={closeCompose} initialBody={composePrefill} trendId={composeTrendId} />}
    </div>
  );
}

/**
 * Bloque de sección del sidebar. Cuando collapsible=true, persiste el
 * estado expandido por nombre de sección en localStorage. Para no-admin,
 * collapsible=false y siempre rendea expandida (sin chevron).
 */
/**
 * Estado activo de un nav item, considerando query params (?mine=) para los
 * sub-items de comunidad. NavLink ignora el search, por eso lo calculamos acá.
 */
function isItemActive(item: NavItem, location: ReturnType<typeof useLocation>): boolean {
  const [path, query] = item.to.split('?');
  const currentMine = new URLSearchParams(location.search).get('mine');

  // "Mi actividad" (única entrada ?mine=) queda activa en cualquier sub-vista
  // de actividad (posts / like / insightful / question).
  if (query && query.startsWith('mine=')) {
    return location.pathname === path && !!currentMine;
  }
  // "Postear" (?compose=1) es una acción, nunca queda marcada como activa.
  if (query && query.startsWith('compose=')) {
    return false;
  }
  // "Comunidad" (sin ?mine=) está activo en el feed y en el detalle de un post,
  // pero NO cuando hay un sub-feed ?mine= activo.
  if (item.to === '/community') {
    return location.pathname.startsWith('/community') && !currentMine;
  }
  if (item.activeAlso?.some((p) => location.pathname.startsWith(p))) return true;
  return item.end ? location.pathname === item.to : location.pathname.startsWith(item.to);
}

function NavSectionBlock({
  section, collapsible,
}: {
  section: NavSection;
  collapsible: boolean;
}) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const storageKey = `sidebar-section-${section.section}`;

  // Abre el modal "Postear" sobre la pantalla actual (sin navegar): solo
  // agrega ?compose=1 a la URL; DashboardLayout lo renderiza globalmente.
  function openCompose() {
    const next = new URLSearchParams(searchParams);
    next.set('compose', '1');
    setSearchParams(next);
  }
  const [expanded, setExpanded] = useState<boolean>(() => {
    if (!collapsible) return true;
    // Si estás en una de sus pantallas, la sección se abre para mostrarla.
    if (section.items.some((item) => isItemActive(item, location))) return true;
    try {
      const v = localStorage.getItem(storageKey);
      return v === null ? !section.collapsedByDefault : v === '1';
    } catch {
      return !section.collapsedByDefault;
    }
  });

  function toggle() {
    setExpanded((v) => {
      const next = !v;
      try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }

  // Sección sin nombre (ej. "Inicio"/Comunidad arriba): sin encabezado y
  // siempre visible (no colapsable).
  const hasHeader = section.section.trim().length > 0;
  const showItems = !hasHeader || expanded;

  return (
    <div className="mb-4">
      {hasHeader && (collapsible ? (
        <button
          type="button"
          onClick={toggle}
          className="mb-1.5 flex w-full cursor-pointer items-center justify-between gap-2 rounded-md px-3 py-2.5 text-[11px] font-bold uppercase tracking-widest text-brand-primary hover:bg-[#EEF7F8] active:bg-[#E1EFF2] transition-colors"
        >
          <span className="inline-flex items-center gap-1.5">
            {section.locked && <Lock className="h-3 w-3 opacity-70" aria-hidden />}
            {section.section}
          </span>
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 transition-transform flex-shrink-0 opacity-70',
              expanded ? '' : '-rotate-90'
            )}
          />
        </button>
      ) : (
        <div
          className="mb-1.5 px-3 py-2.5 text-[11px] font-bold uppercase tracking-widest text-brand-primary"
        >
          {section.section}
        </div>
      ))}

      {showItems && (
        <nav className="space-y-1">
          {section.items.map((item) => {
            const Icon = item.icon;
            const isActive = isItemActive(item, location);
            const baseCls = cn(
              'group flex w-full items-center gap-3 rounded-lg text-sm font-medium transition-all',
              item.indent ? 'pl-9 pr-3 py-1.5 text-[13px]' : 'px-3 py-2',
            );

            // Destino principal (Comunidad): link destacado en verde.
            if (item.highlight) {
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(baseCls, 'bg-brand-energy text-white hover:bg-brand-energy-dark shadow-soft')}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span>{item.label}</span>
                </Link>
              );
            }

            // Item de acción (Postear): botón que abre el modal, no navega.
            // Estilo suave (ya no verde: el verde quedó para Comunidad).
            if (item.cta) {
              return (
                <button
                  key={item.to}
                  type="button"
                  onClick={openCompose}
                  className={cn(baseCls, 'bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/15')}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span>{item.label}</span>
                </button>
              );
            }

            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  baseCls,
                  isActive
                    ? item.indent
                      ? 'bg-brand-primary/10 text-brand-primary'
                      : 'bg-gradient-to-r from-brand-primary to-brand-accent text-white shadow-soft'
                    : 'text-[#10232B] hover:bg-[#EEF7F8]'
                )}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <span>{item.label}</span>
                {item.tag && (
                  <span
                    className={cn(
                      'ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none',
                      isActive ? 'bg-white/20 text-white' : 'bg-brand-accent/10 text-brand-primary',
                    )}
                  >
                    {item.tag}
                  </span>
                )}
                {item.locked && (
                  <Lock
                    aria-label="Solo miembros"
                    className={cn('ml-auto h-3.5 w-3.5 flex-shrink-0', isActive ? 'opacity-90' : 'opacity-50')}
                  />
                )}
              </Link>
            );
          })}
        </nav>
      )}
    </div>
  );
}

/**
 * AccountMenu — avatar/icono arriba a la derecha que desplega un menú con:
 *   - Email del usuario (header informativo).
 *   - Mi cuenta → /account
 *   - Soporte → /account/tickets
 *   - Salir (logout)
 *
 * Antes estos ítems vivían como sección "Cuenta" del sidebar; los movimos
 * aquí para liberar el sidebar (que estaba sobrecargado en cliente/guru)
 * y darle a "Mi cuenta" la ubicación estándar de cualquier producto SaaS.
 *
 * Cierra el dropdown automáticamente al:
 *   - Click fuera del componente.
 *   - Press Escape.
 *   - Click en cualquier item del menú.
 */
function AccountMenu({
  user, avatarUrl, fullName, profileLoading, onLogout, loggingOut,
}: {
  user: SupabaseUser | null;
  /** URL pública de la foto desde profiles.avatar_url (puede ser null). */
  avatarUrl: string | null;
  /** Nombre completo del profile — usado para alt de la foto y para
   *  generar iniciales mejores que las del email. */
  fullName: string | null;
  /** Mientras el perfil carga no sabemos si hay foto: mejor no adelantar iniciales. */
  profileLoading?: boolean;
  onLogout: () => void;
  loggingOut: boolean;
}) {
  const [open, setOpen] = useState(false);
  // Si la URL de la foto falla (404, expirado, etc.) caemos a iniciales.
  const [imgFailed, setImgFailed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Cerrar al click fuera + Escape.
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  // Si cambia el avatar (re-upload), resetear el error para reintentar.
  useEffect(() => { setImgFailed(false); }, [avatarUrl]);

  // Iniciales fallback — preferimos el nombre completo (más reconocible)
  // antes que el email (suele tener prefijos técnicos como "vinicio.garzon").
  const initials = (() => {
    if (fullName && fullName.trim()) {
      return fullName.trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase();
    }
    return (user?.email ?? '?').split('@')[0]!.slice(0, 2).toUpperCase();
  })();

  const showImage = avatarUrl && !imgFailed;

  return (
    <div ref={containerRef} className="relative">
      {/* group/avatar permite que el tooltip de abajo reaccione al hover
          del botón sin interferir con el dropdown. Sólo se muestra cuando
          el menú está cerrado — sino se superpone al panel. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Mi cuenta"
        className={cn(
          'group/avatar peer flex h-10 w-10 items-center justify-center overflow-hidden rounded-full text-sm font-bold transition-all',
          // Sin foto: gradiente brand con iniciales. Con foto: la imagen
          // ocupa todo el círculo y el bg gradient queda solo como anillo
          // durante el load.
          'bg-gradient-to-br from-brand-primary to-brand-accent text-white shadow-sm',
          'hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-accent/40 focus:ring-offset-2',
        )}
      >
        {showImage ? (
          <img
            src={avatarUrl}
            alt={fullName ?? 'Mi avatar'}
            onError={() => setImgFailed(true)}
            className="h-full w-full object-cover"
          />
        ) : profileLoading ? (
          // Mostrar iniciales mientras el perfil viaja hace que el avatar
          // "cambie de identidad" a la vista: primero unas letras, después la
          // foto. Un pulso neutro no promete nada.
          <span className="h-full w-full animate-pulse bg-white/20" />
        ) : (
          initials
        )}
      </button>

      {/* Tooltip — pill flotante debajo del avatar. Aparece sólo cuando
          el dropdown está cerrado y el mouse está sobre el botón.
          `pointer-events-none` evita que el tooltip robe el hover. */}
      {!open && (
        <span
          role="tooltip"
          className={cn(
            'pointer-events-none absolute right-0 top-full z-50 mt-2 whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium text-white opacity-0 shadow-md transition-opacity duration-150',
            'peer-hover:opacity-100 peer-focus-visible:opacity-100',
          )}
          style={{ backgroundColor: '#10232B' }}
        >
          Mi cuenta
        </span>
      )}

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-xl border bg-white shadow-elevated"
          style={{ borderColor: '#DCE7EC' }}
        >
          {/* Header informativo: email del user (no clickable). */}
          <div className="border-b px-4 py-3" style={{ borderColor: '#DCE7EC' }}>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-[#526978]">
              Cuenta
            </p>
            <p className="mt-1 truncate text-sm font-medium" style={{ color: '#10232B' }}>
              {user?.email ?? '—'}
            </p>
          </div>

          {/* Items navegables. onClick={() => setOpen(false)} para cerrar
              cuando el user clickea cualquier opción (el Link igual navega). */}
          <div className="py-1">
            <Link
              to="/account"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-[#EEF7F8]"
              style={{ color: '#10232B' }}
              role="menuitem"
            >
              <User className="h-4 w-4 text-[#526978]" />
              <span>Mi cuenta</span>
            </Link>
            <Link
              to="/account/tickets"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-[#EEF7F8]"
              style={{ color: '#10232B' }}
              role="menuitem"
            >
              <LifeBuoy className="h-4 w-4 text-[#526978]" />
              <span>Soporte</span>
            </Link>
          </div>

          {/* Logout en footer separado — acción destructiva, vale el divisor. */}
          <div className="border-t" style={{ borderColor: '#DCE7EC' }}>
            <button
              type="button"
              onClick={() => { setOpen(false); onLogout(); }}
              disabled={loggingOut}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-[#FEE7E7] disabled:opacity-60"
              style={{ color: '#10232B' }}
              role="menuitem"
            >
              <LogOut className="h-4 w-4 text-[#526978]" />
              <span>{loggingOut ? 'Cerrando sesión…' : 'Salir'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
